import React, { useEffect, useRef, useState } from "react";
import Navbar from "../Navbar";
import { showNotification } from "../../utils/notifikasi";
import { db } from "../../firebase";
import { ref, onValue } from "firebase/database";
import { AlertTriangle, CheckCircle, ShieldAlert, AlertCircle,
  Thermometer, Droplets, Wind, Gauge, 
  Volume2, Sun
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import RealtimeHourlyChart from "../RealtimeHourlyChart";

const SENSOR_KEYS = {
  suhu: {label: "Suhu", unit: "°C", icon: <Thermometer className="w-6 h-6" style={{ color: "#EF4444" }} />},
  kelembapan: {label: "Kelembapan",  unit: "%", icon: <Droplets className="w-6 h-6" style={{color: "#3B82F6"}} />},
  co2: {label: "Gas CO₂",  unit: "ppm", icon: <Gauge className="w-6 h-6" style={{color: "#10B981"}} />},
  co: {label: "Asap",  unit: "ppm", icon: <Wind className="w-6 h-6" style={{color: "#6B7280"}} />},
  kebisingan: {label: "Kebisingan",  unit: "dB", icon: <Volume2 className="w-6 h-6" style={{color: "#8B5CF6"}} />},
  cahaya: {label: "Intensitas Cahaya",unit: "lux", icon: <Sun className="w-6 h-6" style={{color: "#ffd52c"}} />},
};

export default function MainDashboard() {
  const [deviceMap, setDeviceMap] = useState({});
  const [kelasData, setKelasData] = useState({});
  const [ambangBatas, setAmbangBatas] = useState({});
  const [peringatan, setPeringatan] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [historis, setHistoris] = useState({});
  const navigate = useNavigate();
  const lastStatus = useRef({});
  const notifHariIni = useRef({});
  // const cooldown = 5 * 60 * 1000; // 5 menit

  useEffect(() => {
    if ("Notification" in window) {
      Notification.requestPermission().then((permission) => {
        console.log("Permission:", permission);
      });
    }
  }, []);

  useEffect(() => {
    if (!kelasData) return;

    const today = new Date().toISOString().split("T")[0];

    Object.entries(kelasData).forEach(([namaKelas]) => {
      const status = getKelasStatus(namaKelas);
      const prevStatus = lastStatus.current[namaKelas];

      // reset jika hari berganti
      if (notifHariIni.current[namaKelas] !== today) {
        lastStatus.current[namaKelas] = null;
        notifHariIni.current[namaKelas] = today;
      }

      // hanya trigger saat berubah ke bahaya
      if (status === "bahaya" && prevStatus !== "bahaya") {
        showNotification("Peringatan Bahaya!", `${namaKelas} dalam kondisi tidak optimal. Segera ditindaklanjuti.`);
        lastStatus.current[namaKelas] = status;
      }

      // simpan status terakhir
      lastStatus.current[namaKelas] = status;
    });
  }, [kelasData]);

  useEffect(() => {
    const kelasRef = ref(db, "kelas");
    const ambangRef = ref(db, "ambangBatas");
    const mappingRef = ref(db, "deviceMapping");
    const historiRef = ref(db, "dataHistoris");

    onValue(kelasRef, (snapshot) => setKelasData(snapshot.val() || {}));
    onValue(ambangRef, (snapshot) => setAmbangBatas(snapshot.val() || {}));
    onValue(mappingRef, (snapshot) => setDeviceMap(snapshot.val() || {}));
    onValue(historiRef, (snapshot) => setHistoris(snapshot.val() || {}));

    setTimeout(() => setIsLoaded(true), 80);
  }, []);

  useEffect(() => {
    if (!historis) return;

    const today = new Date();
    const tanggalHariIni = today.getFullYear() + "-" + 
      String(today.getMonth() + 1).padStart(2, "0") + "-" +
      String(today.getDate()).padStart(2, "0");
    
    const dataHariIni = historis[tanggalHariIni];
    if (!dataHariIni) {
      setPeringatan([]);
      return;
    }

    const peringatanMap = {};

    console.log(Object.values(dataHariIni));
    Object.values(dataHariIni).forEach((sensor) => {
      if (!sensor || !sensor.Kelas) return;
      const namaKelas = sensor.Kelas;

      const pesanList = [];
      let levelTertinggi = null; // "bahaya" > "waspada"

      const catatLevel = (lvl) => {
        if (lvl === "bahaya") levelTertinggi = "bahaya";
        else if (lvl === "waspada" && levelTertinggi !== "bahaya") {
          levelTertinggi = "waspada"
        }
      };

      if (sensor.status_akhir === 2) {
        catatLevel("bahaya");
      } else if (sensor.status_akhir === 1) {
        catatLevel("waspada");
      }

      // Sensor
      Object.keys(SENSOR_KEYS).forEach((key) => {
        const status = sensor.prediksi_per_sensor?.[key];

        if (status === "bahaya") {
          catatLevel("bahaya");
          pesanList.push(`${key.toUpperCase()}: bahaya`);
        } else if (status === "waspada") {
          catatLevel("waspada");
          pesanList.push(`${key.toUpperCase()}: tidak nyaman`);
        }
      });

      if (levelTertinggi) {
        peringatanMap[namaKelas] = { kelas: namaKelas, level: levelTertinggi, pesanList };
      }
    });

    const newPeringatan = Object.values(peringatanMap);

    // Urutkan: bahaya dulu
    newPeringatan.sort((a, b) => {
      if (a.level === "bahaya" && b.level !== "bahaya") return -1;
      if (a.level !== "bahaya" && b.level === "bahaya") return 1;
      return 0;
    });

    setPeringatan(newPeringatan);
  }, [historis]);

  const hitungStatusAmbang = (val, batas) => {
    if (!batas) return "aman";

    const cekRange = (obj) =>
      val >= Number(obj.min) && val <= Number(obj.max);

    if (batas.bahaya && cekRange(batas.bahaya)) return "bahaya";
    if (batas.bahayabatasbawah && cekRange(batas.bahayabatasbawah)) return "bahaya";
    if (batas.bahayabatasatas && cekRange(batas.bahayabatasatas)) return "bahaya";

    if (batas.waspada && cekRange(batas.waspada)) return "waspada";
    if (batas.waspadabatasbawah && cekRange(batas.waspadabatasbawah)) return "waspada";
    if (batas.waspadabatasatas && cekRange(batas.waspadabatasatas)) return "waspada";

    return "aman";
  };

  const getKelasStatus = (namaKelas) => {
    const sensor = kelasData[namaKelas];
    if (!sensor || !ambangBatas) return "aman";
    if (sensor.status_akhir === 2) return "bahaya";
    if (sensor.status_akhir === 1) return "waspada";
    return "aman";
  };

  const getStatusCardClass = (status) => {
    switch (status) {
      case "aman": return "bg-green-50 border-green-300 hover:border-green-500";
      case "waspada": return "bg-yellow-50 border-yellow-300 hover:border-yellow-500";
      case "bahaya": return "bg-red-50 border-red-300 hover:border-red-500";
      default: return "bg-gray-50 border-gray-200";
    }
  };

  const getStatusDotClass = (status) => {
    switch (status) {
      case "aman": return "bg-green-500";
      case "waspada": return "bg-yellow-400";
      case "bahaya": return "bg-red-500";
      default: return "bg-gray-400";
    }
  };

  const getSensorCardBorder = (status) => {
    switch (status) {
      case "bahaya": return "border-red-300 bg-red-50";
      case "waspada": return "border-yellow-300 bg-yellow-50";
      default: return "border-green-200 bg-green-50";
    }
  };

  const getSensorValueColor = (status) => {
    switch (status) {
      case "bahaya": return "text-red-600";
      case "waspada": return "text-yellow-600";
      default: return "text-green-600";
    }
  };

  const jumlahBahaya = peringatan.filter((p) => p.level === "bahaya").length;
  const jumlahWaspada = peringatan.filter((p) => p.level === "waspada").length;

  //console.log(Object.keys(kelasData));

  return (
    <div className={`min-h-screen bg-slate-50 transition-all duration-700 ease-out overflow-hidden ${
        isLoaded ? "opacity-100" : "opacity-0"
      }`}
    >
      <Navbar />

      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-hidden">

        {/* === Grid Kelas === */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {Object.entries(deviceMap)
            .sort(([, a], [, b]) => {
              const parseKelas = (nama) => {
                const match = nama.match(/Kelas\s*(\d+)([A-Z]?)/i);

                return {
                  nomor: match ? parseInt(match[1], 10) : 999,
                  huruf: match ? (match[2] || "") : "",
                };
              };

              const kelasA = parseKelas(a);
              const kelasB = parseKelas(b);

              // urut nomor kelas
              if (kelasA.nomor !== kelasB.nomor) {
                return kelasA.nomor - kelasB.nomor;
              }
              // urut huruf
              return kelasA.huruf.localeCompare(kelasB.huruf);
            })
            .map(([deviceId, namaKelas], i) => {
              const sensor = kelasData[namaKelas];
              const status = getKelasStatus(namaKelas);

              return (
                <div
                  key={deviceId}
                  className={`border-2 ${getStatusCardClass(status)} rounded-2xl shadow-sm p-4 flex flex-col items-center justify-between gap-3 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5`}
                >
                  <h3 className="text-sm sm:text-base font-bold text-gray-800 text-center leading-tight">
                    {namaKelas}
                  </h3>
                  
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${getStatusDotClass(status)}`} />
                    <span className="text-xs sm:text-sm capitalize font-medium text-gray-700">
                      {status === "bahaya" ? "Tidak Sehat" : status === "waspada" ? "Tidak Nyaman" : "Aman"}
                    </span>
                  </div>

                  <button
                    onClick={() => navigate(`/dashboard/${namaKelas}`)}
                    className="w-full px-3 py-1.5 bg-primaryDark hover:bg-primary active:bg-blue-800 text-white text-xs font-semibold rounded-xl transition-colors"
                  >
                    Cek Kondisi
                  </button>
                </div>
              );
            })
          }
        </div>

        {/* === Chart + Panel Peringatan === */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-8">
          {/* Chart */}
          <div className="lg:col-span-3">
            <RealtimeHourlyChart />
          </div>

          {/* Panel Peringatan */}
          <div className="flex flex-col bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            {/* Header panel */}
            <div className={`px-5 py-4 flex flex-col gap-3 flex-shrink-0 ${
              jumlahBahaya > 0
                ? "bg-red-600"
                : jumlahWaspada > 0
                ? "bg-yellow-500"
                : "bg-emerald-600"
              }`}
            >
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-white">Panel Peringatan</h3>
              </div>

              {/* Badge ringkasan */}
              {peringatan.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  {jumlahBahaya > 0 && (
                    <span className="bg-white/25 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {jumlahBahaya} Bahaya
                    </span>
                  )}
                  {jumlahWaspada > 0 && (
                    <span className="bg-white/25 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {jumlahWaspada} Tidak Nyaman
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Daftar peringatan */}
            <div className="overflow-y-auto flex-1 max-h-80 divide-y divide-gray-100">
              {peringatan.length > 0 ? (
                peringatan.map((item, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-3 px-5 py-3 ${
                      item.level === "bahaya"
                        ? "bg-red-50 hover:bg-red-100"
                        : "bg-yellow-50 hover:bg-yellow-100"
                    } transition-colors`}
                  >
                    <div className="mt-0.5 flex-shrink-0">
                      {item.level === "bahaya" ? (
                        <ShieldAlert size={16} className="text-red-500" />
                      ) : (
                        <AlertTriangle size={16} className="text-yellow-500" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p
                        className={`text-xs font-bold uppercase tracking-wide ${
                          item.level === "bahaya" ? "text-red-600" : "text-yellow-600"
                        }`}
                      >
                        {item.kelas}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                        {item.pesanList.join(" · ")}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-10 px-6 gap-3">
                  <CheckCircle size={40} className="text-green-500" />
                  <p className="text-sm font-semibold text-green-700 text-center">
                    Semua kelas dalam kondisi aman
                  </p>
                  <p className="text-xs text-gray-400 text-center">
                    Tidak ada peringatan aktif saat ini
                  </p>
                </div>
              )}
            </div>

            {/* Footer — total peringatan */}
            {peringatan.length > 0 && (
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex-shrink-0">
                <p className="text-xs text-gray-500 text-center">
                  Total {peringatan.length} peringatan aktif
                </p>
              </div>
            )}
          </div>
        </div>

        {/* === Data Sensor Terkini === */}
        <div className="mt-8 bg-white rounded-2xl shadow-md border border-gray-100 p-6">
          <h3 className="text-base font-bold text-gray-700 mb-5">
            Data Sensor Terkini Per Hari
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(SENSOR_KEYS).map(([sensorKey, {label, unit, icon}]) => {
              const allVals = Object.values(kelasData)
                .map((k) => {
                  const v = k ? k[sensorKey] : undefined;
                  return v !== undefined ? Number(v) : undefined;
                })
                .filter((v) => v !== undefined && !isNaN(v));

              const avg = allVals.length > 0 ? allVals.reduce((a, b) => a + b, 0) / allVals.length : 0;
              const min = allVals.length > 0 ? Math.min(...allVals) : 0;
              const max = allVals.length > 0 ? Math.max(...allVals) : 0;

              const batas = ambangBatas[sensorKey];
              const status = hitungStatusAmbang(avg, batas);

              return (
                <div
                  key={sensorKey}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 ${getSensorCardBorder(status)}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-base">{icon}</span>
                    <h4 className="text-sm font-bold text-gray-600 tracking-wider">{label}</h4>
                  </div>
                  {/* Statistik */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {/* RERATA */}
                    <div className="text-center p-2">
                      <p className="text-xs font-semibold text-gray-500 mb-1">
                        AVG
                      </p>

                      <p className={`text-2xl font-bold ${getSensorValueColor(status)}`}>
                        {avg.toFixed(1)}
                        <span className="text-xs text-gray-300 ml-1">{unit}</span>
                      </p>
                    </div>

                    {/* MIN */}
                    <div className="text-center p-2">
                      <p className="text-xs font-semibold text-gray-500 mb-1">
                        MIN
                      </p>

                      <p className={`text-2xl font-bold ${getSensorValueColor(status)}`}>
                        {min.toFixed(1)}
                        <span className="text-xs text-gray-300 ml-1">{unit}</span>
                      </p>
                    </div>

                    {/* MAX */}
                    <div className="text-center p-2">
                      <p className="text-xs font-semibold text-gray-500 mb-1">
                        MAX
                      </p>

                      <p className={`text-2xl font-bold ${getSensorValueColor(status)}`}>
                        {max.toFixed(1)}
                        <span className="text-xs text-gray-300 ml-1">{unit}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <div className={`w-2 h-2 rounded-full ${status === "bahaya"
                        ? "bg-red-500 animate-pulse"
                        : status === "waspada"
                        ? "bg-yellow-400"
                        : "bg-green-500"
                      }`}
                    />
                    <p className={`text-xs font-medium capitalize ${getSensorValueColor(status)}`}>
                      {status === "bahaya"
                        ? "Bahaya"
                        : status === "waspada"
                        ? "Tidak Nyaman"
                        : "Aman"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}